import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Controller } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import FormField from '../../../../core/components/FormField.tsx';
import SelectField from '../../../../core/components/Fields/SelectField.tsx';
import { SUPPORTED_PRODUCT_TYPES, getProductTypeConfig } from '../types.ts';
import { DOCUMENT_TYPE } from '../../../../document/constants.ts';
import { SmartInboxConfigStepProps } from '../SmartInboxConfigSteps.tsx';
import { withIcon } from '../../../../core/components/Icon.tsx';
import { faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';

const SpinnerIcon = withIcon(faSpinnerThird);

const ProductTypesFormFields: React.FC<SmartInboxConfigStepProps> = ({
    form,
    integration,
    channels,
    isLoading,
    isCreateMode,
}) => {
    const { t } = useTranslation('integrations');

    const getChannelsForType = (type: DOCUMENT_TYPE) => {
        const config = getProductTypeConfig(type);
        return config ? channels?.[config.channelKey] || [] : [];
    };

    if (isCreateMode) {
        const availableProductTypes = SUPPORTED_PRODUCT_TYPES.filter((type) => getChannelsForType(type).length > 0);

        return (
            <div className="flex flex-col gap-6">
                {availableProductTypes.map((type) => (
                    <Controller
                        key={type}
                        name={`productConfigs.${type}.channelId`}
                        control={form.control}
                        render={({ field }) => (
                            <FormField
                                label={
                                    <span className="flex items-center gap-2">
                                        <FontAwesomeIcon icon={getProductTypeConfig(type)!.icon} />
                                        {t(`smartInbox.productTypes.${type.toLowerCase()}`)}
                                    </span>
                                }
                            >
                                <SelectField
                                    {...field}
                                    options={getChannelsForType(type).map((channel) => ({
                                        value: channel.id,
                                        label: channel.name,
                                    }))}
                                    renderOption={(option) => option.label}
                                    placeholder={t('smartInbox.form.productConfig.channelPlaceHolder')}
                                    disabled={isLoading}
                                />
                            </FormField>
                        )}
                    />
                ))}
            </div>
        );
    }

    const configuredProducts = (integration?.productConfigs || [])
        .filter((config) => SUPPORTED_PRODUCT_TYPES.includes(config.documentType as DOCUMENT_TYPE))
        .sort(
            (a, b) =>
                SUPPORTED_PRODUCT_TYPES.indexOf(a.documentType as DOCUMENT_TYPE) -
                SUPPORTED_PRODUCT_TYPES.indexOf(b.documentType as DOCUMENT_TYPE)
        );

    if (configuredProducts.length === 0) {
        return <div className="text-content-medium text-sm py-4">{t('smartInbox.config.products.noProducts')}</div>;
    }

    return (
        <div className="flex flex-col gap-4">
            {isLoading && (
                <div className="w-full h-full flex justify-center items-center">
                    <SpinnerIcon spin className="text-3xl text-content-weak" />
                </div>
            )}
            {!isLoading &&
                configuredProducts.map((config) => {
                    const productConfig = getProductTypeConfig(config.documentType as DOCUMENT_TYPE);
                    if (!productConfig) return null;

                    return (
                        <div
                            key={config.channelId}
                            className="border border-line-weak rounded-md p-4 bg-surface-neutral"
                        >
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-surface-strong flex items-center justify-center">
                                    <FontAwesomeIcon
                                        icon={productConfig.icon}
                                        className="text-content-medium text-lg"
                                    />
                                </div>
                                <div className="flex flex-col gap-0.5">
                                    <span className="text-sm font-medium text-content-strong">
                                        {t(`smartInbox.productTypes.${config.documentType.toLowerCase()}`)}
                                    </span>
                                    <span className="text-sm text-content-medium">{config.channelName}</span>
                                </div>
                            </div>
                        </div>
                    );
                })}

            <p className="text-sm text-content-medium mt-2">{t('smartInbox.config.products.hint')}</p>
        </div>
    );
};

export default ProductTypesFormFields;
